import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, ArrowRight, Wallet, Lock } from "lucide-react";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import FloatingBookCTA from "@/components/site/FloatingBookCTA";
import LeadCaptureForm from "@/components/lead/LeadCaptureForm";
import { submitCalculator } from "@/lib/lead-api";
import usePageMeta from "@/lib/use-page-meta";

const VISA_FEE = 500;
const DOCS_PER_PERSON = 350; // apostille + sworn translation per applicant (varies by # of financial docs)
const TAX_CONSULTATION = 200;
const MISC_ADMIN = 200;

function insuranceCost(age, s1) {
  if (s1 === "yes") return 0;
  if (s1 === "soon") return 1000;
  if (age === "70+") return 2200;
  if (age === "65-69") return 1800;
  if (age === "55-64") return 1400;
  return 1200; // under-55
}

function calculate(inputs) {
  const apps = Number(inputs.applicants) || 1;
  const visa = VISA_FEE * apps;
  const docs = DOCS_PER_PERSON * apps;
  const ins = insuranceCost(inputs.age, inputs.s1) * apps;
  const tax = inputs.tax === "yes" ? TAX_CONSULTATION : 0;
  const misc = MISC_ADMIN;

  const base = visa + docs + ins + tax + misc;
  // Range: ±15%
  const low = Math.round((base * 0.9) / 100) * 100;
  const high = Math.round((base * 1.15) / 100) * 100;

  return {
    low,
    high,
    breakdown: { visa, docs, insurance: ins, tax, misc, base },
  };
}

const Field = ({ label, k, options, value, onChange }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.2em] text-[var(--muted)] font-medium mb-2">{label}</label>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`calc-field-${k}`}>
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            onClick={() => onChange(k, o.value)}
            className={`px-4 py-3 rounded-xl text-sm font-medium transition-all border ${
              active
                ? "bg-[var(--terracotta)] text-[var(--cream)] border-[var(--terracotta)]"
                : "bg-white text-[var(--ink)] border-black/10 hover:border-[var(--terracotta)]/40"
            }`}
            data-testid={`calc-opt-${k}-${o.value}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  </div>
);

export default function Calculator() {
  usePageMeta(
    "NLV Cost Calculator | Get to Spain",
    "Free personalised cost estimate for your Spanish Non-Lucrative Visa move — visa fees, documents, health insurance and more."
  );
  const [inputs, setInputs] = useState({
    applicants: "1",
    age: "55-64",
    s1: "no",
    consulate: "london",
    tax: "yes",
  });
  const [unlocked, setUnlocked] = useState(false);
  const [name, setName] = useState("");

  const estimate = useMemo(() => calculate(inputs), [inputs]);

  const handleSubmit = async (n, email, consent) => {
    await submitCalculator({
      name: n,
      email,
      inputs,
      estimate,
      consent_marketing: consent,
      source: "/calculator",
    });
    setName(n);
    setUnlocked(true);
  };

  const update = (k, v) => setInputs((s) => ({ ...s, [k]: v }));

  return (
    <main className="min-h-screen bg-[var(--cream)] text-[var(--ink)]">
      <SiteNav />

      <section className="pt-28 pb-20 lg:pt-32 lg:pb-24 editorial relative overflow-hidden">
        <div className="absolute -top-32 -right-40 w-[600px] h-[600px] rounded-full bg-[var(--terracotta)]/10 blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -left-32 w-[400px] h-[400px] rounded-full bg-[var(--gold)]/15 blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <Link to="/tools" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--terracotta)]" data-testid="calc-back">
            <ArrowLeft className="w-4 h-4" /> Back to free tools
          </Link>

          <div className="mt-8 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--cream-deep)] border border-[var(--terracotta)]/20 text-xs uppercase tracking-[0.2em] text-[var(--terracotta-deep)] font-medium">
              <Wallet className="w-3.5 h-3.5" /> Free cost calculator
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--navy)] mt-6 leading-[1.04]" data-testid="calc-headline">
              What will your <em className="text-[var(--terracotta)]">NLV move</em> actually cost?
            </h1>
            <p className="mt-6 text-lg text-[var(--ink-soft)] leading-relaxed">
              An honest, personalised range based on your situation. Updates live as you change the
              inputs. No solicitor inflation, no hidden fees.
            </p>
          </div>

          <div className="mt-12 grid lg:grid-cols-5 gap-6">
            {/* INPUTS */}
            <div className="lg:col-span-3 rounded-3xl bg-white p-7 lg:p-8 shadow-xl border border-black/5 space-y-6">
              <Field label="How many applicants?" k="applicants" value={inputs.applicants} onChange={update} options={[
                { value: "1", label: "Just me" },
                { value: "2", label: "Couple" },
                { value: "3", label: "Couple + 1" },
              ]} />
              <Field label="Age band of main applicant" k="age" value={inputs.age} onChange={update} options={[
                { value: "under-55", label: "Under 55" },
                { value: "55-64", label: "55 – 64" },
                { value: "65-69", label: "65 – 69" },
                { value: "70+", label: "70+" },
              ]} />
              <Field label="S1 eligible? (state pension age)" k="s1" value={inputs.s1} onChange={update} options={[
                { value: "yes", label: "Yes" },
                { value: "soon", label: "Within 12mo" },
                { value: "no", label: "No" },
              ]} />
              <Field label="UK consulate" k="consulate" value={inputs.consulate} onChange={update} options={[
                { value: "london", label: "London" },
                { value: "manchester", label: "Manchester" },
                { value: "edinburgh", label: "Edinburgh" },
              ]} />
              <Field label="Include tax-advisor consultation?" k="tax" value={inputs.tax} onChange={update} options={[
                { value: "yes", label: "Yes (recommended)" },
                { value: "no", label: "Skip" },
              ]} />
            </div>

            {/* RESULT */}
            <div className="lg:col-span-2 relative">
              <div className="sticky top-28 rounded-3xl bg-[var(--navy)] text-[var(--cream)] p-7 lg:p-8 shadow-2xl overflow-hidden grain">
                <div className="absolute top-0 right-0 w-56 h-56 bg-[var(--terracotta)]/30 rounded-full blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="text-xs uppercase tracking-[0.3em] text-[var(--gold)] font-medium">Estimated total</div>

                  {unlocked ? (
                    <>
                      <div className="mt-4 font-serif text-4xl lg:text-5xl leading-none" data-testid="calc-range">
                        £{estimate.low.toLocaleString()} <span className="text-[var(--cream)]/60">–</span> £{estimate.high.toLocaleString()}
                      </div>
                      <div className="mt-6 space-y-2.5 text-sm text-[var(--cream)]/90">
                        <Row label={`Visa fee × ${inputs.applicants}`} value={estimate.breakdown.visa} />
                        <Row label="Documents (apostille + translation)" value={estimate.breakdown.docs} />
                        <Row label={estimate.breakdown.insurance > 0 ? "Private health insurance (12mo)" : "Health insurance (S1)"} value={estimate.breakdown.insurance} />
                        {estimate.breakdown.tax > 0 && <Row label="Tax consultation" value={estimate.breakdown.tax} />}
                        <Row label="Admin, photos, travel, TIE" value={estimate.breakdown.misc} />
                      </div>
                      <p className="mt-6 text-xs text-[var(--cream)]/70 leading-relaxed">
                        <strong className="text-[var(--cream)]/90">Please note:</strong> private health insurance varies significantly based on age and any pre-existing health conditions — your quote may sit outside this range. Document translation costs also vary depending on how many financial documents you need translated. Excludes Carley's NLV Approved consultancy fee (which avoids the much higher cost of a solicitor).
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="mt-4 font-serif text-4xl lg:text-5xl leading-none flex items-center gap-3 text-[var(--cream)]/40">
                        <Lock className="w-7 h-7" />
                        £—,—
                      </div>
                      <p className="mt-5 text-sm text-[var(--cream)]/85 leading-relaxed">
                        Enter your details below to unlock your personalised range and the
                        line-by-line breakdown.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* EMAIL GATE or CTA */}
          {!unlocked ? (
            <div className="mt-12 rounded-3xl bg-white p-7 lg:p-10 shadow-xl border border-black/5 max-w-2xl mx-auto">
              <h2 className="font-serif text-2xl lg:text-3xl text-[var(--navy)]">Unlock your full breakdown</h2>
              <p className="mt-2 text-sm text-[var(--ink-soft)]">
                Free. Your estimate appears straight away and Carley will email you a copy.
              </p>
              <div className="mt-6">
                <LeadCaptureForm
                  onSubmit={handleSubmit}
                  ctaLabel="Show me my estimate"
                  fbEvent="Lead"
                  fbEventParams={{ content_name: "NLV Cost Calculator", source: "/calculator" }}
                />
              </div>
            </div>
          ) : (
            <div className="mt-12 rounded-[2rem] p-8 lg:p-12 bg-[var(--cream-deep)]/60 border border-black/5">
              <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">
                Thanks {name.split(" ")[0]}
              </div>
              <h2 className="mt-3 font-serif text-3xl lg:text-4xl text-[var(--navy)] leading-tight">
                Want Carley to <em>refine that figure</em> for your exact situation?
              </h2>
              <p className="mt-4 text-[var(--ink-soft)] max-w-2xl">
                The numbers above are honest estimates. On a free call, Carley will tighten them
                using your real ages, income type, property situation and insurance quotes — and
                map out the order to spend in.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a href="/nlv-approved#book" className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium" data-testid="calc-cta-book">
                  <Calendar className="w-5 h-5" /> Book a free call
                </a>
                <Link to="/quiz" className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[var(--navy)]/25 text-[var(--navy)] hover:bg-[var(--navy)] hover:text-[var(--cream)] font-medium">
                  Am I NLV-ready? <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      <SiteFooter />
      <FloatingBookCTA />
    </main>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-[var(--cream)]/15 pb-1.5">
    <span>{label}</span>
    <span className="font-serif text-[var(--cream)] font-medium">£{value.toLocaleString()}</span>
  </div>
);
